import { Icon } from '@iconify/react'

import { Tooltip } from '@pepper/ui'

import Card from './Card/Card'
import Table from './Card/Table/Table'
import CompetitorLogo from './Card/CompetitorLogo'
import FAQs from 'container/SEOTools/components/FAQs/FAQs'
import InsightSection from './InsightSection/InsightSection'
import PageContentLength from './Card/PageContentLength/PageContentLength'
import KeywordTag from 'container/SEOTools/components/KeywordTag/KeywordTag'
import { getMetaCopy } from './utils'
import type {
  GapReportDocument,
  EditorialAssessment,
} from '@pepper/utils/src/types/seo'

import s from './Insights.module.css'

type Props = {
  document: GapReportDocument['document']
}

const MAX_META_TITLE_LENGTH = 60
const MIN_META_TITLE_LENGTH = 50
const MAX_META_DESCRIPTION_LENGTH = 160
const MIN_META_DESCRIPTION_LENGTH = 150
const SCORE_THRESHOLD = 2.5

const editorialTitleMap: Record<keyof EditorialAssessment, string> = {
  content: 'Readability',
  title: 'Title Assessment',
  outline: 'Outline Review',
  heading_subheading: 'Heading and Subheading Evaluation',
  eat: 'E.A.T. Evaluation',
  seo: 'SEO Evaluation',
  tones: 'Tone Evaluation',
}

const Insights = ({ document }: Props) => {
  const keywordTableData = document?.keywords?.map(keyword => ({
    Keyword: keyword.keyword,
    'Search Volume': new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
    }).format(keyword.volume),
    Competitors: keyword.competitors?.map(url => (
      <CompetitorLogo key={url} className={s.logoStack} competitor={{ url }} />
    )),
  }))

  const termTableData =
    document?.terms &&
    document?.terms.map(term => ({
      Terms: term.term,
      'Typical Frequency':
        term.frequency === '1' ? `1 time` : `${term.frequency} times`,
      '': term.heading_count > 0 && (
        <div className={s.tagsWrapper}>
          <KeywordTag type="heading" />
        </div>
      ),
    }))

  const competitorLenMean =
    Math.floor(document?.competitor_stats.length.mean / 100) * 100
  const roundedPageLength = Math.floor(document?.page_length / 100) * 100
  const lenMin = competitorLenMean - 100
  const lenMax = competitorLenMean + 100
  const pageContentLengthText =
    roundedPageLength > competitorLenMean
      ? `Good work! Your page is more comprehensive than the average ranking pages.`
      : document?.page_length < competitorLenMean
      ? `Add ${
          competitorLenMean - document?.page_length
        } words of content to your page.`
      : `Great work! Your page is in the ideal length of the average ranking pages.`

  return (
    <div className={s.wrapper}>
      <InsightSection
        heading="SEO Insights"
        subHeading="Detailed checks for your blog's SEO health."
      >
        <Card
          type={
            document?.meta.title.length <= MAX_META_TITLE_LENGTH &&
            document?.meta.title.length >= MIN_META_TITLE_LENGTH &&
            document?.meta.title.keyword_present
              ? 'success'
              : 'error'
          }
          title="Meta Title"
          subTitle={getMetaCopy(document, 'title')}
        >
          <p className={s.text}>
            {document?.meta.title.content.length > 0
              ? document?.meta.title.content
              : 'NA'}
          </p>
        </Card>
        <Card
          type={
            document?.meta.description.length <= MAX_META_DESCRIPTION_LENGTH &&
            document?.meta.description.length >= MIN_META_DESCRIPTION_LENGTH &&
            document?.meta.description.keyword_present
              ? 'success'
              : 'error'
          }
          title="Meta Description"
          subTitle={getMetaCopy(document, 'description')}
        >
          <p className={s.text}>
            {document?.meta.description.content.length > 0
              ? document?.meta.description.content
              : 'NA'}
          </p>
        </Card>
        <Card
          type="error"
          title="Missing Keywords and Terms"
          subTitle="Add these keywords and terms in your content to increase your total volume."
        >
          <div className={s.tableContainer}>
            {keywordTableData && <Table data={keywordTableData} />}
            {termTableData && <Table data={termTableData} />}
          </div>
        </Card>
        <Card
          type={
            (roundedPageLength > lenMin && roundedPageLength < lenMax) ||
            roundedPageLength > lenMax
              ? 'success'
              : 'error'
          }
          title="Page Content Length"
          subTitle={pageContentLengthText}
        >
          <PageContentLength document={document} />
        </Card>
        {document?.questions &&
          (document.page_questions?.length ? (
            <Card
              type="success"
              title="Great work"
              subTitle="You have an FAQ schema present on your website. We recommend answering the following questions in your content."
            >
              <FAQs questions={document?.questions} />
            </Card>
          ) : (
            <Card
              type="error"
              title="Answer these questions"
              subTitle="Add a FAQ schema to your blog page. Answer these questions in your content"
            >
              <FAQs questions={document?.questions} />
            </Card>
          ))}
      </InsightSection>
      <InsightSection
        heading="Content Editorial Insights"
        subHeading="Elevate content quality with editorial feedback."
      >
        {Object.keys(document?.editorial_assessment ?? {}).map(
          assessmentKey => (
            <Card
              key={assessmentKey}
              type={
                +document?.editorial_assessment[assessmentKey].score >
                SCORE_THRESHOLD
                  ? 'success'
                  : 'error'
              }
              title={editorialTitleMap[assessmentKey]}
              {...(assessmentKey === 'eat' && {
                icon: (
                  <Tooltip
                    title="Google ranks content that showcases
expertise (E), builds authority (A), and
maintains trustworthiness (T)."
                  >
                    <Icon icon="lucide:lightbulb" width={16} height={16} />
                  </Tooltip>
                ),
              })}
            >
              {document?.editorial_assessment[assessmentKey].feedback}

              {assessmentKey === 'content' && (
                <div className={s.readability}>
                  <span>Your Page:</span>
                  <b>{Math.round(document.reading_grade)}th grade</b>
                  <span>Ranking Pages:</span>
                  <b>
                    {Math.round(document.competitor_stats.reading_grade.mean)}th
                    grade
                  </b>
                </div>
              )}
            </Card>
          )
        )}
      </InsightSection>
    </div>
  )
}

export default Insights
